import {
  phoneNumberValidator,
  websiteValidator,
  zipCodeValidator,
  phoneNumberOptValidator,
  SSNOptValidator,
  websiteWwwRequiredValidator,
  notNegativeOneValidator,
  SSNValidatorTesting,
  atLeastOneCharsLongOrBlank,
  zipCodeValidatorOptional,
  atLeastThreeCharsLongOrBlank,
  taxIdValidator,
} from "../misc/regex";
import * as Yup from "yup";
import { eighteenYearsAgo } from "../calculators";
import { Utils } from "../utils";

const makeInOoNetworkTest = (
  isInNetwork: boolean,
  isIndividualPolicy: boolean
): Yup.TestFunction => {
  const prefix = isInNetwork ? "inNetw" : "ooNetw";
  const alternateModifier = isIndividualPolicy ? "Family" : "";
  const alternateFields = [
    "OopMax",
    "OopMaxRemaining",
    "Deductible",
    "DeductibleRemaining",
  ].map((fld) => `${prefix}${alternateModifier}${fld}`);
  return (value, context) => {
    if (
      context.parent.policyInNetwork !== isInNetwork ||
      typeof value === "number"
    ) {
      return true;
    }
    return alternateFields.every(
      (fld) => typeof context.parent[fld] === "number"
    );
  };
};

const makeCustomConfigTest = (
  fieldName: "bannerText" | "referenceNumberHelpText" | "referenceNumberName"
): Yup.TestFunction => {
  const others = [
    "bannerText",
    "referenceNumberName",
    "referenceNumberHelpText",
  ].filter((value) => value !== fieldName);
  return (value, context) => {
    let isRequired = Utils.isPopulatedValue(
      context.parent.payNowExternalAccount
    );
    if (others.some((other) => Utils.isPopulatedValue(context.parent[other]))) {
      isRequired = true;
    }
    return Utils.isPopulatedValue(value) || !isRequired;
  };
};

export const commonFields = {
  // Note: All validation of ".min(1) effectively makes the field required without using ".required(). Does so silently without adding a "Required" message. Only needed for fields that don't need add'l validation
  REQUIRED_STRING: Yup.string().min(1), // Generic. Used for fields that only need to be required, but isn't included in this 'commonFields' object.
  STREET_ADDRESS_1: Yup.string()
    .min(3)
    .matches(
      atLeastThreeCharsLongOrBlank,
      "Street Address 1 must be at least 3 characters long"
    ),
  STREET_ADDRESS_1_OPTIONAL: Yup.string().matches(
    atLeastThreeCharsLongOrBlank,
    "Street Address 1 must be at least 3 characters long"
  ),
  STREET_ADDRESS_2: Yup.string().matches(
    atLeastThreeCharsLongOrBlank,
    "Street Address 2 must be at least 3 characters long"
  ),
  CITY: Yup.string()
    .min(1)
    .matches(
      atLeastOneCharsLongOrBlank,
      "City must be at least 1 characters long"
    ),
  CITY_OPTIONAL: Yup.string().matches(
    atLeastOneCharsLongOrBlank,
    "City must be at least 1 characters long"
  ),
  STATE: Yup.string()
    .min(1)
    .matches(notNegativeOneValidator, "Must select a value"),
  STATE_OPTIONAL: Yup.string().matches(
    atLeastOneCharsLongOrBlank,
    "Must select a value"
  ),
  ZIP: Yup.string()
    .trim()
    .min(5)
    .matches(zipCodeValidator, "Must be a number 5 characters long"),
  ZIP_OPTIONAL: Yup.string().matches(
    zipCodeValidatorOptional,
    "Must be a number 5 characters long"
  ),
  WEBSITE: Yup.string()
    .matches(websiteWwwRequiredValidator, '"www." is required')
    .matches(websiteValidator, "Website Format is Invalid"),
  FIRST_NAME: Yup.string().min(1).max(50, "Must be 50 characters or less"),
  MIDDLE_NAME: Yup.string(),
  LAST_NAME: Yup.string().min(1).max(50, "Must be 50 characters or less"),
  PATIENT_NAME_OPT: Yup.string()
    .min(0)
    .max(50, "Must be 50 characters or less"),
  EMAIL: Yup.string().min(1).email("Email Format is Invalid"),
  RETYPE_EMAIL: Yup.string().oneOf(
    [Yup.ref("email"), null],
    "Email and Email Confirm must Match"
  ), // first email field must be called 'email'
  PHONE: Yup.string().matches(phoneNumberValidator, "Phone format is Invalid"),
  RETYPE_PHONE: Yup.string()
    .matches(phoneNumberValidator, "Invalid format, please try again")
    .oneOf(
      [Yup.ref("phoneNumber"), null],
      "Phone Number and Confirm Phone Number fields must match, please try again"
    ), // first mobile phone field must be called 'phoneNumber'
  PHONE_OPT: Yup.string()
    .matches(phoneNumberOptValidator, "Phone format is Invalid")
    .nullable(),
  MOBILE_PHONE: Yup.string()
    .min(1)
    .matches(phoneNumberValidator, "Mobile Phone Format is Invalid"),
  RETYPE_MOBILE_PHONE: Yup.string()
    .matches(phoneNumberValidator, "Invalid format, please try again")
    .oneOf(
      [Yup.ref("mobilePhone"), null],
      "Mobile Phone and Mobile Phone Confirm must Match"
    ), // first mobile phone field must be called 'mobilePhone'
  DATE: Yup.string().min(1), // TODO: proper validation
  OPTIONAL_DATE: Yup.string().nullable(),
  SSN: Yup.string().matches(SSNValidatorTesting, "SSN format invalid"), //This is just for testing purposes, will change to the "Real" SNN Validatior
  SSN_OPT: Yup.string().matches(SSNOptValidator, "SSN format invalid"),
  TAX_ID: Yup.string()
    .matches(taxIdValidator, "Invalid tax Id format")
    .required("This field is required"),
  REQUIRED_DROPDOWN: Yup.string()
    .min(1)
    .matches(notNegativeOneValidator, "Must select a value"), // use when you want a dropdown to be 'required'.
  ROUTING_NUMBER: Yup.string().matches(
    /[\d]{2}-?[\d]{7}/,
    "Invalid Routing Number Format"
  ),
  ACCOUNT_NUMBER: Yup.string().matches(/[\d]{5,17}/, "Invalid Account Format"), // US bank accounts can be from 6 to 17 digits. Source: https://bit.ly/3AWKlI5
  RETYPE_ACCOUNT_NUMBER: Yup.string().oneOf(
    [Yup.ref("accountNumber"), null],
    "Account Mismatch"
  ), // first account number field must be called 'accountNumber',
  ONLY_NUMERIC_VALUES: Yup.string()
    .min(1)
    .matches(/^[0-9]*([.][0-9]*)?$/, "Invalid number"),
  EIGHTEEN_YEARS_OR_OLDER: Yup.date().max(
    eighteenYearsAgo(),
    "Must be at least 18 yrs old"
  ),
  REQUIRED_NUMBER_VOB: Yup.number()
    .nullable(true)
    .required("This field is required"),
  REQUIRED_NUMBER: Yup.number().required("This field is required"),
  REQUIRED_POSITIVE_NUMBER: Yup.number()
    .positive("Must be a positive number")
    .required("This field is required"),
  REQUIRED_POSITIVE_NUMBER_ZERO_INCLUSIVE: Yup.number()
    .min(0, "Must be a positive number")
    .required("This field is required"),
  NOT_REQUIRED_NULLABLE_NUMBER: Yup.number()
    .nullable(true)
    .notRequired()
    .transform((_, val) => (val === Number(val) ? val : null)),

  IN_NETWORK_INDIVIDUAL_POLICY_ITEM: Yup.number()
    .nullable(true)
    .notRequired()
    .transform((_, val) => (val === Number(val) ? val : null))
    .test(
      "inNetworkIndividualPolicyItem",
      "This field is required",
      makeInOoNetworkTest(true, true)
    ),

  OUT_OF_NETWORK_INDIVIDUAL_POLICY_ITEM: Yup.number()
    .nullable(true)
    .notRequired()
    .transform((_, val) => (val === Number(val) ? val : null))
    .test(
      "inNetworkIndividualPolicyItem",
      "This field is required",
      makeInOoNetworkTest(false, true)
    ),

  IN_NETWORK_FAMILY_POLICY_ITEM: Yup.number()
    .nullable(true)
    .notRequired()
    .transform((_, val) => (val === Number(val) ? val : null))
    .test(
      "inNetworkIndividualPolicyItem",
      "This field is required",
      makeInOoNetworkTest(true, false)
    ),

  OUT_OF_NETWORK_FAMILY_POLICY_ITEM: Yup.number()
    .nullable(true)
    .notRequired()
    .transform((_, val) => (val === Number(val) ? val : null))
    .test(
      "inNetworkIndividualPolicyItem",
      "This field is required",
      makeInOoNetworkTest(false, false)
    ),
};

export const location = {
  streetAddress1: commonFields.STREET_ADDRESS_1,
  streetAddress2: commonFields.STREET_ADDRESS_2,
  city: commonFields.CITY,
  state: commonFields.REQUIRED_DROPDOWN,
  zipCode: commonFields.ZIP,
};



export const facility = {
  facilityName: commonFields.REQUIRED_STRING,
  streetAddress1: commonFields.STREET_ADDRESS_1,
  city: commonFields.CITY,
  state: commonFields.REQUIRED_DROPDOWN,
  zipCode: commonFields.ZIP,
  website: commonFields.WEBSITE,
  phone: commonFields.PHONE,
  primaryContactFirstName: commonFields.FIRST_NAME,
  primaryContactLastName: commonFields.LAST_NAME,
  primaryContactAddress1: commonFields.STREET_ADDRESS_1,
  primaryContactCity: commonFields.CITY,
  primaryContactState: commonFields.REQUIRED_DROPDOWN,
  primaryContactZipCode: commonFields.ZIP,
  primaryContactEmail: commonFields.EMAIL,
  reTypePrimaryContactEmail: Yup.string().oneOf(
    [Yup.ref("primaryContactEmail"), null],
    "Email and Email Confirm must Match"
  ),
  primaryContactPhone: commonFields.PHONE,
  primaryContactMobilePhone: commonFields.PHONE,
  reTypePrimaryContactMobilePhone: Yup.string().oneOf(
    [Yup.ref("primaryContactMobilePhone"), null],
    "Mobile Phone and Mobile Phone Confirm must Match"
  ),
  taxIdNumber: commonFields.TAX_ID,
  // externalAccount: commonFields.REQUIRED_DROPDOWN,
  payOutFreq: commonFields.REQUIRED_STRING,
  payOutDay: Yup.string()
    .when("payOutFreq", {
      is: "weekly",
      then: commonFields.REQUIRED_DROPDOWN,
    })
    .when("payOutFreq", {
      is: "monthly",
      then: commonFields.REQUIRED_DROPDOWN,
    }),
  payNowExternalAccount: Yup.mixed().nullable(),
  bannerText: Yup.string()
    .notRequired()
    .test(
      "bannerText",
      "This field is required",
      makeCustomConfigTest("bannerText")
    ),
  referenceNumberName: Yup.string()
    .notRequired()
    .test(
      "referenceNumberName",
      "This field is required",
      makeCustomConfigTest("referenceNumberName")
    ),
  referenceNumberHelpText: Yup.string()
    .notRequired()
    .test(
      "referenceNumberHelpText",
      "This field is required",
      makeCustomConfigTest("referenceNumberHelpText")
    ),
};







